import { useState } from 'react'
import {
  KeyRound,
  Loader2,
  LogIn,
  MoreVertical,
  Pencil,
  RefreshCw,
  LogOut,
  ShieldCheck,
  ShieldQuestion,
  Trash2,
} from 'lucide-react'
import type { StaticIpAssignmentDto, BrokerConnectionDto, ConnectionStatus } from '@apna/shared'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'

/**
 * The accounts table.
 *
 * Built on trading-journal's own `ui/table` and `ui/dropdown-menu` primitives
 * rather than hand-rolled markup. The hand-rolled version had a real bug: the
 * menu was a positioned `div` INSIDE a cell, so opening it inside the table's
 * `overflow-x-auto` wrapper stretched the row and clipped the menu. Radix
 * renders the content in a portal, so it floats above the table and cannot
 * affect layout.
 *
 * ORDERS replaces the journal's RMS column (RMS is Phase 7 here). It reports
 * whether ORDER capability was proven, which is a different claim from
 * "connected": brokers whitelist order placement by IP but not reads, so a
 * status pill reading "Active" can sit above an account where every order
 * fails.
 */

const pill =
  'inline-flex items-center rounded-md border px-2 py-0.5 text-[10px] font-medium whitespace-nowrap'

const secondaryActionBtn =
  'inline-flex h-8 shrink-0 items-center justify-center gap-1.5 rounded-lg border px-2.5 text-xs font-medium whitespace-nowrap transition-colors hover:bg-accent disabled:opacity-50'

const primaryActionBtn =
  'inline-flex h-8 shrink-0 items-center justify-center gap-1.5 rounded-lg bg-primary px-2.5 text-xs font-medium text-primary-foreground whitespace-nowrap shadow-sm transition hover:opacity-90 disabled:opacity-50'

const iconActionBtn =
  'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-colors hover:bg-accent'

const th =
  'px-3 py-2.5 text-center text-[10px] font-semibold uppercase tracking-wide text-muted-foreground lg:px-4'

function StatusPill({ status }: { status: ConnectionStatus }) {
  switch (status) {
    case 'CONNECTED':
      return <span className={cn(pill, 'border-success/20 bg-success/10 text-success')}>Active</span>
    case 'PENDING':
      return (
        <span className={cn(pill, 'border-border bg-muted text-muted-foreground')}>
          <span className="mr-1.5 inline-block size-1.5 rounded-full bg-muted-foreground" />
          Not verified
        </span>
      )
    case 'DEGRADED':
      return (
        <span className={cn(pill, 'border-warning/20 bg-warning/10 text-warning')}>
          Action required
        </span>
      )
    case 'DISABLED':
      return <span className={cn(pill, 'border-border bg-muted text-muted-foreground')}>Disabled</span>
    case 'AUTH_FAILED':
    default:
      return (
        <span className={cn(pill, 'border-destructive/20 bg-destructive/10 text-destructive')}>
          Error
        </span>
      )
  }
}

function ModeChip({ mode }: { mode: 'LIVE' | 'PAPER' }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-medium',
        mode === 'LIVE' ? 'bg-warning/15 text-warning' : 'bg-secondary text-secondary-foreground',
      )}
    >
      {mode === 'LIVE' ? 'Live' : 'Paper'}
    </span>
  )
}

interface Props {
  rows: BrokerConnectionDto[]
  /**
   * Where each account's orders leave from. Absent means not loaded yet;
   * an entry with a null `endpointId` means DIRECT, which is a real answer
   * and not a missing one.
   */
  assignments?: StaticIpAssignmentDto[]
  loading: boolean
  busyId: string | null
  onReadiness: (row: BrokerConnectionDto) => void
  onLogout: (row: BrokerConnectionDto) => void
  onReconnect: (row: BrokerConnectionDto) => void
  onEdit: (row: BrokerConnectionDto) => void
  onDelete: (row: BrokerConnectionDto) => void
}

export function AccountsTable({
  rows,
  assignments,
  loading,
  busyId,
  onReadiness,
  onLogout,
  onReconnect,
  onEdit,
  onDelete,
}: Props) {
  // Which row's menu is open. Tracked so the trigger can stay highlighted;
  // Radix owns the open/close behaviour itself.
  const [menuFor, setMenuFor] = useState<string | null>(null)

  return (
    <div className="rounded-xl border bg-card shadow-sm">
      <Table containerClassName="rounded-xl" className="min-w-[820px]">
        <TableHeader>
          <TableRow className="border-b bg-muted/40 hover:bg-muted/40">
            <TableHead className={cn(th, 'text-left')}>Holder Name</TableHead>
            <TableHead className={cn(th, 'text-left')}>Broker</TableHead>
            <TableHead className={th}>Status</TableHead>
            <TableHead className={th}>Assigned IP</TableHead>
            <TableHead className={th}>Orders</TableHead>
            <TableHead className={cn(th, 'text-right')}>Actions</TableHead>
          </TableRow>
        </TableHeader>

        <TableBody>
          {loading && (
            <TableRow>
              <TableCell colSpan={6} className="px-4 py-10 text-center">
                <Loader2 size={18} className="mx-auto animate-spin text-muted-foreground" />
              </TableCell>
            </TableRow>
          )}

          {!loading && rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={6} className="px-4 py-10 text-center">
                <p className="text-sm font-medium text-foreground">No accounts connected</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Add a broker to place orders. Credentials are encrypted before they are stored.
                </p>
              </TableCell>
            </TableRow>
          )}

          {rows.map((c) => {
            const busy = busyId === c.id
            /*
             * Three states, not two. A probe that reached the broker but could
             * only read the order book proves credentials, IP whitelisting and
             * endpoint access — it does NOT prove the broker's RMS will accept
             * an order, and labelling it "Verified" next to a green shield is
             * the overclaim this column exists to avoid.
             */
            const orderProven = c.lastReadinessProbe === 'ORDER_MARGIN'
            const reachable = Boolean(c.lastReadinessOkAt) && !orderProven

            return (
              <TableRow key={c.id} className="border-b hover:bg-muted/20" data-testid={`row-${c.id}`}>
                <TableCell className="px-3 py-3 align-middle lg:px-4">
                  <p className="truncate text-sm font-medium">{c.label}</p>
                  {c.tag && <p className="mt-0.5 text-[10px] text-muted-foreground">{c.tag}</p>}
                </TableCell>

                <TableCell className="px-3 py-3 align-top lg:px-4">
                  <div className="flex min-w-0 items-start gap-2.5">
                    <div
                      className="inline-flex shrink-0 items-center justify-center rounded-lg border bg-background"
                      style={{ width: 26, height: 26 }}
                    >
                      <KeyRound className="h-3.5 w-3.5 text-muted-foreground" />
                    </div>

                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <p className="truncate text-sm font-semibold leading-5">{c.providerKey}</p>
                        <ModeChip mode={c.mode} />
                      </div>
                      <div className="mt-1 flex flex-wrap gap-1">
                        <span className="inline-flex items-center rounded-md bg-secondary px-1.5 py-0.5 text-[10px] font-medium text-secondary-foreground">
                          {c.clientCode}
                        </span>
                        {c.interactiveLogin?.isInvestorClient === false && (
                          <span
                            className="inline-flex items-center rounded-md bg-secondary px-1.5 py-0.5 text-[10px] font-medium text-secondary-foreground"
                            title="A dealer login acts for many client codes and has no funds API."
                          >
                            DEALER
                          </span>
                        )}
                        {/*
                          Exactly one account carries prices for this trader
                          (3.21). Coloured rather than grey because "which
                          account is my feed" is the first question when the
                          chain is showing dashes.
                        */}
                        {c.isMasterFeed && (
                          <span
                            className="inline-flex items-center rounded-md bg-primary/15 px-1.5 py-0.5 text-[10px] font-semibold text-primary"
                            title="Live prices come from this account's market-data credentials. Only one account can be the feed."
                          >
                            FEED
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </TableCell>

                <TableCell className="px-3 py-3 text-center align-middle lg:px-4">
                  <StatusPill status={c.status} />
                  {/* Wrapped, not truncated — a one-line clip is what hid
                      "IP address does not match the login IP". */}
                  {c.lastError && (
                    <p
                      className="mx-auto mt-1 line-clamp-3 max-w-[240px] whitespace-pre-wrap break-words text-left text-[10px] leading-snug text-destructive"
                      title={c.lastError}
                    >
                      {c.lastError}
                    </p>
                  )}
                </TableCell>

                <TableCell className="px-3 py-3 text-center align-middle lg:px-4">
                  {/*
                    THE ADDRESS, NOT THE WORD "PLATFORM". This read "Platform"
                    for every account whatever its route, which is the one
                    thing this column must never do: a broker whitelists one
                    IP per set of credentials, and an account sending from an
                    address nobody checked is how every order gets refused.
                    "Not assigned" is a real state — it means DIRECT.
                  */}
                  {(() => {
                    const a = assignments?.find((x) => x.connectionId === c.id)
                    if (!a) return <span className="text-[11px] text-muted-foreground">—</span>
                    return a.endpointId === null ? (
                      <span className="text-[11px] text-muted-foreground">Not assigned</span>
                    ) : (
                      <span className="num text-[12px]">{a.expectedExitIp ?? a.endpointLabel}</span>
                    )
                  })()}
                </TableCell>

                <TableCell className="px-3 py-3 text-center align-middle lg:px-4">
                  <button
                    type="button"
                    onClick={() => onReadiness(c)}
                    disabled={busy}
                    className="inline-flex items-center gap-1 text-[11px] text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50"
                    title={c.readinessDetail ?? 'Probe order capability'}
                    aria-label={`Readiness: ${orderProven ? 'order capability proven' : reachable ? 'broker reachable, order capability unproven' : 'not verified'}`}
                    data-testid={`readiness-${c.id}`}
                  >
                    {busy ? (
                      <Loader2 size={11} className="animate-spin" />
                    ) : orderProven ? (
                      <ShieldCheck size={12} className="text-success" />
                    ) : reachable ? (
                      <ShieldQuestion size={12} className="text-warning" />
                    ) : null}
                    <span
                      className={
                        orderProven ? 'text-success' : reachable ? 'text-warning' : undefined
                      }
                    >
                      {orderProven ? 'Verified' : reachable ? 'Reachable' : 'Not verified'}
                    </span>
                  </button>
                </TableCell>

                <TableCell className="px-3 py-3 align-middle lg:px-4">
                  <div className="flex w-full items-center justify-end gap-1.5 whitespace-nowrap">
                    {c.interactiveLogin ? (
                      <button
                        className={c.status === 'CONNECTED' ? secondaryActionBtn : primaryActionBtn}
                        disabled={busy}
                        onClick={() => onReconnect(c)}
                      >
                        <LogIn className="h-3.5 w-3.5 shrink-0" />
                        {/*
                          "Re-Login" once the account is already connected.
                          "Login" on a live session reads as though nothing is
                          logged in, which is the opposite of what the status
                          pill beside it says — and it makes a harmless
                          refresh look like a step you had forgotten.
                        */}
                        <span className="hidden xl:inline">
                          {busy ? 'Working…' : c.status === 'CONNECTED' ? 'Re-Login' : 'Login'}
                        </span>
                      </button>
                    ) : (
                      <span className="text-[10px] text-warning">no credential</span>
                    )}

                    <DropdownMenu
                      open={menuFor === c.id}
                      onOpenChange={(o) => setMenuFor(o ? c.id : null)}
                    >
                      <DropdownMenuTrigger asChild>
                        <button className={iconActionBtn} aria-label="More actions">
                          <MoreVertical className="h-4 w-4" />
                        </button>
                      </DropdownMenuTrigger>

                      {/* Portalled by Radix, so it floats above the table's
                          overflow wrapper instead of stretching the row. */}
                      <DropdownMenuContent align="end" className="w-52">
                        <DropdownMenuItem onSelect={() => onEdit(c)}>
                          <Pencil className="h-3.5 w-3.5" /> Edit account
                        </DropdownMenuItem>
                        <DropdownMenuItem onSelect={() => onReadiness(c)}>
                          <ShieldCheck className="h-3.5 w-3.5" /> Test order capability
                        </DropdownMenuItem>
                        <DropdownMenuItem onSelect={() => onReconnect(c)}>
                          <RefreshCw className="h-3.5 w-3.5" /> Force re-login
                        </DropdownMenuItem>
                        {/*
                          Only offered when there is a session to end. XTS
                          allows a limited number of concurrent sessions per
                          app key, so an abandoned one holds a slot for
                          nothing.
                        */}
                        {c.interactiveLogin?.hasLiveToken && (
                          <DropdownMenuItem onSelect={() => onLogout(c)}>
                            <LogOut className="h-3.5 w-3.5" /> End broker session
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem variant="destructive" onSelect={() => onDelete(c)}>
                          <Trash2 className="h-3.5 w-3.5" /> Remove account
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </div>
  )
}
